import React from "react";
import axios from "axios";
import moment from "moment";
import HOST_URL from "../../../configs/api";
import toastMessage from "../../../components/toastMessage";
import { Filter } from "../../../components/Filter";
import { Loading } from "../../../components/Loader";
import CardCount from "../../../components/CardCount/CardCount";

// ─── constants ───────────────────────────────────────────────────────────────

const ACTION_META = {
  LOGIN:            { label: "Login",              color: "#1e293b", bg: "#f1f5f9", icon: "fa fa-sign-in",      category: "Sessions" },
  APPLY:            { label: "Loan Application",   color: "#3b82f6", bg: "#eff6ff", icon: "fa fa-file-text",    category: "Loans" },
  APPROVE:          { label: "Approved",           color: "#10b981", bg: "#ecfdf5", icon: "fa fa-check-circle", category: "Loans" },
  REJECT:           { label: "Rejected",           color: "#ef4444", bg: "#fef2f2", icon: "fa fa-times-circle", category: "Loans" },
  DISBURSE:         { label: "Disbursed",          color: "#8b5cf6", bg: "#f5f3ff", icon: "fa fa-money",        category: "Loans" },
  TOPUP:            { label: "Top-Up",             color: "#06b6d4", bg: "#ecfeff", icon: "fa fa-plus-circle",  category: "Loans" },
  UPDATE:           { label: "Updated",            color: "#f59e0b", bg: "#fffbeb", icon: "fa fa-pencil",       category: "Loans" },
  DELETE:           { label: "Deleted",            color: "#ef4444", bg: "#fef2f2", icon: "fa fa-trash",        category: "Loans" },
  ADD_PAYMENT:      { label: "Payment",            color: "#059669", bg: "#ecfdf5", icon: "fa fa-credit-card",  category: "Payments" },
  REVERSE_PAYMENT:  { label: "Payment Reversed",   color: "#f97316", bg: "#fff7ed", icon: "fa fa-undo",         category: "Payments" },
  DEPOSIT:          { label: "Deposit",            color: "#2563eb", bg: "#eff6ff", icon: "fa fa-arrow-down",   category: "Savings" },
  WITHDRAW:         { label: "Withdrawal",         color: "#dc2626", bg: "#fef2f2", icon: "fa fa-arrow-up",     category: "Savings" },
  REVERSE_SAVING:   { label: "Saving Reversed",    color: "#b45309", bg: "#fffbeb", icon: "fa fa-undo",         category: "Savings" },
  ADD_INTEREST:     { label: "Interest",           color: "#7c3aed", bg: "#f5f3ff", icon: "fa fa-percent",      category: "Loans" },
  DELETE_INTEREST:  { label: "Interest Deleted",   color: "#ef4444", bg: "#fef2f2", icon: "fa fa-trash",        category: "Loans" },
  ADD_PENALTY:      { label: "Penalty",            color: "#b91c1c", bg: "#fef2f2", icon: "fa fa-exclamation",  category: "Loans" },
  DELETE_PENALTY:   { label: "Penalty Removed",    color: "#6b7280", bg: "#f9fafb", icon: "fa fa-trash",        category: "Loans" },
  ISSUE_MEMBER_NOS: { label: "Member Nos",         color: "#0891b2", bg: "#ecfeff", icon: "fa fa-id-card-o",    category: "Members" },
  ADD_SHARES:       { label: "Shares",             color: "#0d9488", bg: "#f0fdfa", icon: "fa fa-bar-chart",    category: "Other" },
  ADD_DIVIDEND:     { label: "Dividend",           color: "#1d4ed8", bg: "#eff6ff", icon: "fa fa-gift",         category: "Other" },
  ADD_TRUST:        { label: "Trust",              color: "#4f46e5", bg: "#eef2ff", icon: "fa fa-shield",       category: "Other" },
  UPDATE_SETTINGS:  { label: "Settings",           color: "#6b7280", bg: "#f9fafb", icon: "fa fa-cog",          category: "Other" },
};

const CATEGORY_GROUPS = [
  { key: "all",      label: "All",      icon: "fa fa-th" },
  { key: "Sessions", label: "Sessions", icon: "fa fa-sign-in" },
  { key: "Loans",    label: "Loans",    icon: "fa fa-file-text-o" },
  { key: "Payments", label: "Payments", icon: "fa fa-credit-card" },
  { key: "Savings",  label: "Savings",  icon: "fa fa-bank" },
  { key: "Members",  label: "Members",  icon: "fa fa-users" },
  { key: "Other",    label: "Other",    icon: "fa fa-ellipsis-h" },
];

const getInitials = (name = "") =>
  name.trim().split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const AVATAR_COLORS = ["#6366f1", "#8b5cf6", "#ec4899", "#f43f5e", "#f97316", "#10b981", "#06b6d4", "#3b82f6"];
const getAvatarColor = (name = "") => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const timeAgo = (date) => {
  const diff = moment().diff(moment(date), "seconds");
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return moment(date).format("MMM D");
};

// ─── component ───────────────────────────────────────────────────────────────

class DailyActionsReport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      apps: [],
      appid: null,
      start_date: moment().format("YYYY-MM-DD"),
      end_date: moment().format("YYYY-MM-DD"),
      response: {},
      category: "all",
      data: [],
      summary: { totalActions: 0, byActionType: {} },
      pagination: { page_number: 1, page_limit: 25, total_records: 0, total_pages: 1 },
      isLoading: false,
      isExporting: false,
      autoRefresh: false,
      lastUpdated: null,
      showDownloadMenu: false,
      // tick triggers re-render so "time ago" updates every 15s
      tick: 0,
    };
    this._refreshInterval = null;
    this._tickInterval = null;
  }

  componentDidMount() {
    const { apps } = this.props;
    const f_apps = apps.map((el) => ({ label: el.name, value: el._id }));
    this.setState({ apps: f_apps }, () => {
      if (apps.length === 1) {
        this.onChangeSacco({ label: apps[0].name, value: apps[0]._id });
      }
    });
    this._tickInterval = setInterval(
      () => this.setState((s) => ({ tick: s.tick + 1 })),
      15000
    );
    document.addEventListener("click", this._handleOutsideClick);
  }

  componentWillUnmount() {
    this._stopAutoRefresh();
    clearInterval(this._tickInterval);
    document.removeEventListener("click", this._handleOutsideClick);
  }

  _handleOutsideClick = (e) => {
    if (this.state.showDownloadMenu && !e.target.closest(".btn-group")) {
      this.setState({ showDownloadMenu: false });
    }
  };

  _stopAutoRefresh() {
    if (this._refreshInterval) {
      clearInterval(this._refreshInterval);
      this._refreshInterval = null;
    }
  }

  onChangeSacco = (e) => {
    this.setState({ appid: e, data: [], category: "all" }, () => this.fetchReport(1));
  };

  onChangeStartingDate = (e) => {
    this.setState({ start_date: e.target.value }, () => this.fetchReport(1));
  };

  onChangeEndDate = (e) => {
    this.setState({ end_date: e.target.value }, () => this.fetchReport(1));
  };

  handleResetFilters = () => {
    this.setState(
      {
        start_date: moment().format("YYYY-MM-DD"),
        end_date: moment().format("YYYY-MM-DD"),
        category: "all",
      },
      () => this.fetchReport(1)
    );
  };

  onChangeCategory = (cat) => {
    this.setState({ category: cat }, () => this.fetchReport(1));
  };

  toggleAutoRefresh = () => {
    const { autoRefresh, appid } = this.state;
    if (!autoRefresh && appid?.value) {
      this._refreshInterval = setInterval(() => this.fetchReport(1, true), 30000);
    } else {
      this._stopAutoRefresh();
    }
    this.setState({ autoRefresh: !autoRefresh });
  };

  fetchReport = (page = 1, silent = false) => {
    const { appid, start_date, end_date, category, pagination } = this.state;
    if (!appid?.value) return;

    if (!silent) this.setState({ isLoading: true });

    const categoryActions =
      category === "all"
        ? null
        : Object.entries(ACTION_META)
            .filter(([, v]) => v.category === category)
            .map(([k]) => k)
            .join(",");

    const params = {
      app_id: appid.value,
      fromDate: start_date,
      toDate: end_date,
      page_number: page,
      page_limit: pagination.page_limit,
    };
    if (categoryActions) params.action_types = categoryActions;

    axios({
      method: "GET",
      url: `${HOST_URL.url}/reports/daily-actions`,
      params,
      headers: { authorization: `Bearer ${this.props.ztoken}` },
    })
      .then(({ data }) => {
        this.setState({
          isLoading: false,
          data: data.data || [],
          summary: data.summary || { totalActions: 0, byActionType: {} },
          pagination: data.pagination || this.state.pagination,
          lastUpdated: new Date(),
        });
      })
      .catch((err) => {
        this.setState({ isLoading: false });
        if (!silent) {
          toastMessage("error", err?.response?.data?.message || "Failed to load report");
        }
      });
  };

  toggleDownloadMenu = () => {
    this.setState((s) => ({ showDownloadMenu: !s.showDownloadMenu }));
  };

  exportPdf = () => {
    const { data, start_date, end_date, appid } = this.state;
    this.setState({ showDownloadMenu: false });

    const rows = data.map((r) => {
      const meta = ACTION_META[r.action_type] || { label: r.action_type || r.method };
      return `
        <tr>
          <td>${r.createdAt ? new Date(r.createdAt).toISOString().replace("T", " ").substring(0, 19) : ""}</td>
          <td>${r.userDetails?.username || ""}</td>
          <td>${r.userDetails?.phoneNumber || ""}</td>
          <td>${meta.label}</td>
          <td>${r.resource_type || ""}</td>
          <td>${r.resource_id || ""}</td>
          <td>${r.description || ""}</td>
        </tr>`;
    }).join("");

    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Daily Actions Report</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
        h2 { margin-bottom: 4px; }
        p { margin: 0 0 12px; color: #555; font-size: 11px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #f32323; color: #fff; padding: 6px 8px; text-align: left; font-size: 11px; }
        td { padding: 5px 8px; border-bottom: 1px solid #eee; font-size: 11px; }
        tr:nth-child(even) td { background: #f9f9f9; }
      </style></head>
      <body>
        <h2>Daily Actions Report</h2>
        <p>Period: ${start_date} to ${end_date} &nbsp;|&nbsp; SACCO: ${appid?.label || ""}</p>
        <table>
          <thead>
            <tr>
              <th>Date</th><th>User</th><th>Phone</th><th>Action</th>
              <th>Resource</th><th>Resource ID</th><th>Description</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  _triggerDownload(content, filename, mime) {
    const url = window.URL.createObjectURL(new Blob([content], { type: mime }));
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", filename);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  exportCsv = () => {
    const { appid, start_date, end_date } = this.state;
    this.setState({ isExporting: true, showDownloadMenu: false });

    const filename = `daily_actions_${start_date}_to_${end_date}.csv`;

    if (!appid?.value) {
      const headers = "Date,User,Phone,Action,Resource,Resource ID,Description,Method,Path\n";
      this._triggerDownload(headers, filename, "text/csv");
      this.setState({ isExporting: false });
      return;
    }

    axios({
      method: "GET",
      url: `${HOST_URL.url}/reports/daily-actions/csv`,
      params: { app_id: appid.value, fromDate: start_date, toDate: end_date },
      headers: { authorization: `Bearer ${this.props.ztoken}` },
      responseType: "blob",
    })
      .then(({ data }) => {
        this._triggerDownload(data, filename, "text/csv");
        this.setState({ isExporting: false });
      })
      .catch(() => {
        this.setState({ isExporting: false });
        toastMessage("error", "Failed to export CSV");
      });
  };

  // ── helpers ────────────────────────────────────────────────────────────────

  _getCategoryGrouped() {
    const by = this.state.summary.byActionType || {};
    const grouped = { all: this.state.summary.totalActions || 0, Sessions: 0, Loans: 0, Payments: 0, Savings: 0, Members: 0, Other: 0 };
    Object.entries(by).forEach(([type, cnt]) => {
      const cat = ACTION_META[type]?.category || "Other";
      grouped[cat] = (grouped[cat] || 0) + cnt;
    });
    return grouped;
  }

  // ── renderers ──────────────────────────────────────────────────────────────

  renderMetricCards() {
    const { summary } = this.state;
    const grouped = this._getCategoryGrouped();

    return (
      <div className="row" style={{ marginBottom: 8 }}>
        <CardCount
          className="col-md-2"
          title="Total Actions"
          count={summary.totalActions || 0}
          color="primary"
          icon="fa fa-list-alt"
        />
        <CardCount
          className="col-md-2"
          title="Logins"
          count={grouped.Sessions}
          color="secondary"
          icon="fa fa-sign-in"
        />
        <CardCount
          className="col-md-2"
          title="Loan Actions"
          count={grouped.Loans}
          color="info"
          icon="fa fa-file-text-o"
        />
        <CardCount
          className="col-md-2"
          title="Payments"
          count={grouped.Payments}
          color="success"
          icon="fa fa-credit-card"
        />
        <CardCount
          className="col-md-2"
          title="Savings"
          count={grouped.Savings}
          color="warning"
          icon="fa fa-bank"
        />
        <CardCount
          className="col-md-2"
          title="Other"
          count={grouped.Other}
          color="danger"
          icon="fa fa-ellipsis-h"
        />
      </div>
    );
  }

  renderCategoryTabs() {
    const { category } = this.state;
    const grouped = this._getCategoryGrouped();

    return (
      <ul className="nav nav-tabs" style={{ padding: "0 12px", borderBottom: "1px solid #ddd", marginBottom: 0 }}>
        {CATEGORY_GROUPS.map((g) => (
          <li key={g.key} className={category === g.key ? "active" : ""}>
            <a
              href={`#cat-${g.key}`}
              onClick={(e) => { e.preventDefault(); this.onChangeCategory(g.key); }}
            >
              <i className={g.icon} style={{ marginRight: 5 }}></i>
              {g.label}
              <span
                className="badge"
                style={{
                  marginLeft: 6,
                  backgroundColor: category === g.key ? "rgb(243,35,35)" : "#e0e0e0",
                  color: category === g.key ? "#fff" : "#555",
                  fontSize: 10,
                }}
              >
                {grouped[g.key] || 0}
              </span>
            </a>
          </li>
        ))}
      </ul>
    );
  }

  renderToolbar() {
    const { autoRefresh, lastUpdated, isExporting, appid, pagination, showDownloadMenu } = this.state;

    return (
      <div style={styles.toolbar}>
        <div style={styles.toolbarLeft}>
          {autoRefresh && (
            <span style={styles.livePill}>
              <span style={styles.liveDot}></span>
              LIVE
            </span>
          )}
          {lastUpdated && (
            <span style={styles.updatedAt}>
              <i className="fa fa-clock-o" style={{ marginRight: 4 }}></i>
              {moment(lastUpdated).format("HH:mm:ss")}
            </span>
          )}
          <span style={styles.recordCount}>
            {pagination.total_records.toLocaleString()} record{pagination.total_records !== 1 ? "s" : ""}
          </span>
        </div>
        <div style={styles.toolbarRight}>
          <button
            onClick={this.toggleAutoRefresh}
            className={`btn btn-sm ${autoRefresh ? "btn-success" : "btn-default"}`}
            style={{ marginRight: 8 }}
          >
            <i className={`fa fa-refresh ${autoRefresh ? "fa-spin" : ""}`} style={{ marginRight: 4 }}></i>
            {autoRefresh ? "Live On" : "Auto Refresh"}
          </button>
          <div className="btn-group" style={{ position: "relative" }}>
            <button
              className="btn btn-sm btn-default dropdown-toggle"
              onClick={this.toggleDownloadMenu}
            >
              <i
                className={`fa ${isExporting ? "fa-spinner fa-spin" : "fa-download"}`}
                style={{ marginRight: 4 }}
              ></i>
              Download <span className="caret"></span>
            </button>
            {showDownloadMenu && (
              <ul className="dropdown-menu dropdown-menu-right" style={{ display: "block", minWidth: 140 }}>
                <li>
                  <a
                    href="#csv"
                    onClick={(e) => { e.preventDefault(); this.exportCsv(); }}
                  >
                    <i className="fa fa-file-excel-o" style={{ marginRight: 6 }}></i>
                    Export CSV
                  </a>
                </li>
                <li>
                  <a
                    href="#pdf"
                    onClick={(e) => { e.preventDefault(); this.exportPdf(); }}
                  >
                    <i className="fa fa-file-pdf-o" style={{ marginRight: 6 }}></i>
                    Export PDF
                  </a>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderTemplateRecord() {
    const samples = [
      { action_type: "LOGIN",       userDetails: { username: "Jane Wanjiku",   phoneNumber: "0712 345 678" }, description: "User logged in",               resource_type: "SESSION",  resource_id: null,            createdAt: new Date(Date.now() - 120000) },
      { action_type: "APPROVE",     userDetails: { username: "Peter Kamau",    phoneNumber: "0722 456 789" }, description: "Loan approved",                resource_type: "LOAN",     resource_id: "LN-0042",       createdAt: new Date(Date.now() - 300000) },
      { action_type: "ADD_PAYMENT", userDetails: { username: "Grace Achieng",  phoneNumber: "0733 567 890" }, description: "Loan repayment recorded",      resource_type: "LOAN",     resource_id: "LN-0039",       createdAt: new Date(Date.now() - 600000) },
      { action_type: "DEPOSIT",     userDetails: { username: "Samuel Otieno",  phoneNumber: "0745 678 901" }, description: "Savings deposit recorded",     resource_type: "SAVING",   resource_id: null,            createdAt: new Date(Date.now() - 900000) },
      { action_type: "APPLY",       userDetails: { username: "Mary Njeri",     phoneNumber: "0756 789 012" }, description: "Loan application submitted",   resource_type: "LOAN",     resource_id: null,            createdAt: new Date(Date.now() - 1800000) },
    ];

    return (
      <div style={{ position: "relative" }}>
        <div style={styles.feed}>
          {samples.map((row, i) => {
            const meta = ACTION_META[row.action_type];
            const name = row.userDetails.username;
            return (
              <div key={i} style={{ ...styles.feedRow, opacity: 0.35, filter: "grayscale(30%)", pointerEvents: "none" }}>
                <div style={{ ...styles.colorBar, background: meta.color }}></div>
                <div style={{ ...styles.avatar, background: getAvatarColor(name) }}>{getInitials(name)}</div>
                <div style={styles.feedBody}>
                  <div style={styles.feedTop}>
                    <strong style={styles.feedName}>{name.toUpperCase()}</strong>
                    <span style={{ ...styles.badge, background: meta.bg, color: meta.color, border: `1px solid ${meta.color}40` }}>
                      <i className={meta.icon} style={{ marginRight: 4 }}></i>{meta.label}
                    </span>
                  </div>
                  <div style={styles.feedDesc}>{row.description}{row.resource_id && <span style={styles.resId}> · {row.resource_id}</span>}</div>
                  <div style={styles.feedPhone}>{row.userDetails.phoneNumber}</div>
                </div>
                <div style={styles.feedTime}>
                  <div style={styles.timeAgo}>{timeAgo(row.createdAt)}</div>
                  <div style={styles.timeAbs}>{moment(row.createdAt).format("HH:mm:ss")}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={styles.templateOverlay}>
          <i className="fa fa-database" style={{ fontSize: 28, color: "#94a3b8", marginBottom: 8 }}></i>
          <p style={{ margin: 0, color: "#64748b", fontSize: 13, fontWeight: 600 }}>No activity recorded yet</p>
          <p style={{ margin: "4px 0 0", color: "#94a3b8", fontSize: 12 }}>Actions will appear here as your team uses the system</p>
        </div>
      </div>
    );
  }

  renderFeed() {
    const { data } = this.state;

    if (!data.length) {
      return this.renderTemplateRecord();
    }

    return (
      <div style={styles.feed}>
        {data.map((row, i) => {
          const meta = ACTION_META[row.action_type] || {
            label: `${row.method} ${row.path}`,
            color: "#6b7280",
            bg: "#f9fafb",
            icon: "fa fa-circle",
          };
          const name = row.userDetails?.username || "Unknown";
          const isToday = moment(row.createdAt).isSame(moment(), "day");
          const timeStr = isToday
            ? timeAgo(row.createdAt)
            : moment(row.createdAt).format("MMM D, HH:mm");

          return (
            <div key={row._id || i} style={styles.feedRow}>
              {/* colour bar */}
              <div style={{ ...styles.colorBar, background: meta.color }}></div>

              {/* avatar */}
              <div style={{ ...styles.avatar, background: getAvatarColor(name) }}>
                {getInitials(name)}
              </div>

              {/* main content */}
              <div style={styles.feedBody}>
                <div style={styles.feedTop}>
                  <strong style={styles.feedName}>{name.toUpperCase()}</strong>
                  <span
                    style={{
                      ...styles.badge,
                      background: meta.bg,
                      color: meta.color,
                      border: `1px solid ${meta.color}40`,
                    }}
                  >
                    <i className={meta.icon} style={{ marginRight: 4 }}></i>
                    {meta.label}
                  </span>
                </div>
                <div style={styles.feedDesc}>
                  {row.description || `${row.method} ${row.path}`}
                  {row.resource_id && (
                    <span style={styles.resId}> · {row.resource_id.substring(0, 10)}…</span>
                  )}
                </div>
                {row.userDetails?.phoneNumber && (
                  <div style={styles.feedPhone}>{row.userDetails.phoneNumber}</div>
                )}
              </div>

              {/* timestamp */}
              <div style={styles.feedTime}>
                <div style={styles.timeAgo}>{timeStr}</div>
                <div style={styles.timeAbs}>
                  {moment(row.createdAt).format("HH:mm:ss")}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  renderPagination() {
    const { pagination } = this.state;
    if (pagination.total_pages <= 1) return null;

    const cur = pagination.page_number;
    const tot = pagination.total_pages;
    const start = Math.max(1, cur - 2);
    const end = Math.min(tot, cur + 2);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);

    return (
      <div style={styles.paginationRow}>
        <ul className="pagination pagination-sm" style={{ margin: 0 }}>
          <li className={cur === 1 ? "disabled" : ""}>
            <a href="#prev" onClick={(e) => { e.preventDefault(); if (cur > 1) this.fetchReport(cur - 1); }}>
              «
            </a>
          </li>
          {start > 1 && <li className="disabled"><a href="#dots">…</a></li>}
          {pages.map((p) => (
            <li key={p} className={p === cur ? "active" : ""}>
              <a href={`#p${p}`} onClick={(e) => { e.preventDefault(); this.fetchReport(p); }}>
                {p}
              </a>
            </li>
          ))}
          {end < tot && <li className="disabled"><a href="#dots2">…</a></li>}
          <li className={cur === tot ? "disabled" : ""}>
            <a href="#next" onClick={(e) => { e.preventDefault(); if (cur < tot) this.fetchReport(cur + 1); }}>
              »
            </a>
          </li>
        </ul>
        <span style={{ marginLeft: 12, color: "#64748b", fontSize: 12 }}>
          Page {cur} of {tot}
        </span>
      </div>
    );
  }

  render() {
    const { apps, appid, start_date, end_date, response, isLoading } = this.state;

    return (
      <div className="content-cont">
        <div className="panel-body" style={{ paddingLeft: 0 }}>

          {/* ── standard Filter (same as every other page) ── */}
          <Filter
            appid={appid}
            apps={apps}
            start_date={start_date}
            end_date={end_date}
            response={response}
            onChangeStartingDate={this.onChangeStartingDate}
            onChangeEndDate={this.onChangeEndDate}
            onChangeSacco={this.onChangeSacco}
            showReset={!!appid?.value}
            handleResetFilters={this.handleResetFilters}
          />

          <div className="col-md-12">

            {/* ── metric cards — always visible ── */}
            {this.renderMetricCards()}

            {/* ── activity panel ── */}
            <div className="panel panel-default" style={{ marginTop: 8 }}>
              <div className="panel-body" style={{ padding: 0 }}>

                {/* category tabs */}
                {this.renderCategoryTabs()}

                {/* toolbar */}
                {this.renderToolbar()}

                {/* feed or loader */}
                {!appid?.value ? (
                  this.renderTemplateRecord()
                ) : isLoading ? (
                  <Loading message="Loading activity..." icon="fa fa-spinner fa-spin" />
                ) : (
                  this.renderFeed()
                )}

                {/* pagination */}
                {this.renderPagination()}
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }
}

// ─── styles ──────────────────────────────────────────────────────────────────

const styles = {
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    borderBottom: "1px solid #f1f5f9",
    flexWrap: "wrap",
    gap: 8,
  },
  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  toolbarRight: {
    display: "flex",
    alignItems: "center",
  },
  livePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    background: "#dcfce7",
    color: "#16a34a",
    border: "1px solid #86efac",
    borderRadius: 20,
    padding: "2px 10px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#16a34a",
    display: "inline-block",
  },
  updatedAt: {
    fontSize: 12,
    color: "#94a3b8",
  },
  recordCount: {
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
  },
  feed: {
    padding: "4px 0",
  },
  feedRow: {
    display: "flex",
    alignItems: "flex-start",
    padding: "12px 16px",
    borderBottom: "1px solid #f8fafc",
    gap: 12,
    position: "relative",
  },
  colorBar: {
    width: 4,
    minHeight: 44,
    borderRadius: 4,
    flexShrink: 0,
    alignSelf: "stretch",
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  feedBody: {
    flex: 1,
    minWidth: 0,
  },
  feedTop: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 2,
  },
  feedName: {
    fontSize: 13,
    color: "#0f172a",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
  },
  feedDesc: {
    fontSize: 12,
    color: "#64748b",
    marginTop: 1,
  },
  resId: {
    fontFamily: "monospace",
    fontSize: 11,
    color: "#94a3b8",
  },
  feedPhone: {
    fontSize: 11,
    color: "#94a3b8",
    marginTop: 2,
  },
  feedTime: {
    flexShrink: 0,
    textAlign: "right",
    minWidth: 72,
  },
  timeAgo: {
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    whiteSpace: "nowrap",
  },
  timeAbs: {
    fontSize: 10,
    color: "#94a3b8",
    whiteSpace: "nowrap",
    marginTop: 2,
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
  },
  paginationRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 16px",
    borderTop: "1px solid #f1f5f9",
    flexWrap: "wrap",
  },
  templateOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(2px)",
    zIndex: 2,
  },
};

export default DailyActionsReport;
